/**
 * Client-side Layout Document validation. Uses the exact same schema the
 * backend validates against (`docs/schema/layout.schema.json`), so the editor
 * can show errors live instead of only on save. The server re-validates on
 * write — this is a convenience, not the gate.
 */

import Ajv2020, { type AnySchema, type ErrorObject } from 'ajv/dist/2020.js';
import addFormats from 'ajv-formats';
import schema from '@layout-schema';
import type { LayoutDocument, LayoutModule, LayoutRegion, LayoutWidget } from './types.js';

const ajv = new Ajv2020({ allErrors: true, strict: true, strictRequired: false });
addFormats(ajv);
const validate = ajv.compile(schema as unknown as AnySchema);

export interface SchemaIssue {
  path: string;
  message: string;
}

function describe(err: ErrorObject): SchemaIssue {
  const path = err.instancePath || '/';
  if (err.keyword === 'additionalProperties') {
    return { path, message: `unexpected property "${String(err.params.additionalProperty)}"` };
  }
  if (err.keyword === 'required') {
    return { path, message: `missing "${String(err.params.missingProperty)}"` };
  }
  return { path, message: err.message ?? 'invalid' };
}

export function validateLayout(document: unknown): SchemaIssue[] {
  return validate(document) ? [] : (validate.errors ?? []).map(describe);
}

let seq = 0;
/** A fresh document-unique id, e.g. for a duplicated region or widget. */
export const nextId = (prefix: string): string => `${prefix}-${Date.now().toString(36)}-${(seq += 1)}`;

export function blankDocument(width = 1920, height = 1080): LayoutDocument {
  return {
    schemaVersion: 1,
    id: nextId('lay'),
    dimensions: { width, height, orientation: width >= height ? 'landscape' : 'portrait' },
    regions: [blankRegion(width, height)],
  };
}

export function blankRegion(canvasW: number, canvasH: number): LayoutRegion {
  return {
    id: nextId('r'),
    name: 'Region',
    rect: { unit: 'px', x: 0, y: 0, width: canvasW, height: canvasH },
    zIndex: 0,
    loop: true,
    playlist: { id: nextId('pl'), items: [] },
  };
}

/** Native modules the editor offers directly. Installed marketplace modules
 * (ADR-0015) are added to this list at runtime by the editor. */
export const EDITOR_MODULES: LayoutModule[] = ['image', 'video', 'text', 'web', 'clock'];

export function blankWidget(module: string, defaults?: Record<string, unknown>): LayoutWidget {
  const widget: LayoutWidget = { id: nextId('w'), module };
  if (module === 'video') {
    widget.media = [];
    widget.useMediaDuration = true;
    return widget;
  }
  widget.duration = 10;
  if (module === 'image') widget.media = [];
  else if (module === 'text') widget.options = { text: 'Hello' };
  else if (module === 'web') widget.options = { url: 'https://example.com' };
  else if (module === 'clock') widget.options = { format: 'HH:mm' };
  else widget.options = { ...(defaults ?? {}) }; // marketplace module
  return widget;
}
